import { blobToFile } from "~/js/network/asset-storage";
import { axiosClient } from "~/js/network/axiosClient";
import { getMimeTypeByExtension } from "~/js/network/mime-type-handler";
import * as network from "~/js/network/network-handler";
import { config } from "~/js/network/network-handler";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { BaseApi } from "~/solidJs/shared/api/baseApi";
import { getPrivateMessageFromError, type IFileType, isAbortError } from "~/solidJs/shared/helpers";
import { addHeightWidthToFile } from "../helpers/validateImages";

export type DownloadLinkResponse = {
	download_link: string;
	download_link_proxy: string;
	file_name: string;
	height: number | null;
	width: number | null;
};
export type DownloadLinksResponse = DownloadLinkResponse & {
	file_id: number;
};

export class FileApi extends BaseApi {
	static assetsEndpoint = `${BaseApi.baseUrlV2}/storage-object/storage-object`;
	static checkEndpoint = `${BaseApi.baseUrlV2}/storage-object/storage-objects`;
	/**
	 * @description get server fileId from File object
	 * @param file
	 */
	static getFileIdInLocalStorage(file: IFileType): null | number {
		console.log(`Try find local storage record of this file (${file.name})`);

		const id = file.fileId;
		if (id === undefined) return null;
		return id;
	}

	static rememberFileInLocalStorage(file: IFileType, id: number) {
		console.log(`Remember local storage record for this file (${file.name})`);
		file.fileId = id;
	}

	static async getLinkToAwsByFileId(id: number, abortSignal?: AbortSignal) {
		const params = {
			obj_id: id
		};
		const url = `${FileApi.assetsEndpoint}/download-link`;
		const timeStart = Date.now();
		console.log(`GET-request url with query: ${url}`);
		const response = await axiosClient.axiosClient.get<DownloadLinkResponse>(url, {
			params,
			headers: network.standardHeaders(),
			...(abortSignal ? { signal: abortSignal } : {})
		});
		const timeEnd = Date.now();
		EventAnalytics.getFileLink(timeEnd - timeStart, id);
		console.log("Response: ", response);
		if (response.status > 399) {
			throw `Storage Error ${response.status} ${response.statusText}`;
		}
		const urlToAws = response.data;
		if (!urlToAws || !urlToAws.download_link) {
			throw `File with ID: ${id} isn't on storage`;
		}
		return urlToAws;
	}

	/**
	 * @description request links list to aws
	 * @param ids
	 * @param abortSignal
	 */
	static async getLinksToAwsListByFileId(ids: number[], abortSignal?: AbortSignal) {
		if (ids.length < 1) return [];
		const url = `${FileApi.assetsEndpoint}/download-links`;

		const response = await axiosClient.axiosClient.post<{ links: DownloadLinksResponse[] }>(
			url,
			{
				link_ids: ids
			},
			{
				...(abortSignal ? { signal: abortSignal } : {})
			}
		);

		if (response.status > 399) {
			throw `Storage Error ${response.status} ${response.statusText}`;
		}

		return response.data.links;
	}

	/**
	 * @param file {File}  File to push to remote storage
	 * @param options {Object} Options for file upload
	 * @param options.abortSignal {AbortSignal} abortSignal
	 * @param options.temporary {boolean} Mark file as temporary (will be auto-deleted if not saved in project)
	 * @return {Promise<number>} File storage ID
	 * */
	static async pushFileAsync(
		file: IFileType,
		options?: { abortSignal?: AbortSignal; temporary?: boolean }
	): Promise<number> {
		try {
			console.log("Pushing file to storage...", options?.temporary ? "(temporary)" : "");
			const body = new FormData();
			body.append("fileobject", file, file.name);
			body.append("Content-Type", "multipart/form-data");

			// Add temporary flag if specified
			if (options?.temporary) {
				body.append("is_temporary", "true");
			}

			const response = await axiosClient.axiosClient.post<{ file_obj_id: number }>(
				FileApi.assetsEndpoint,
				body,
				{
					...(options?.abortSignal ? { signal: options.abortSignal } : {})
				}
			);

			console.log("Response: ", response);
			if (response.status > 399) {
				throw `Storage Error ${response.status} ${response.statusText}`;
			}
			const responseJson = response.data;
			console.log("Response Json: ", responseJson);
			const id = responseJson.file_obj_id;
			if (!id) {
				console.error("Server responded with empty storage ID");
				throw "Server responded with empty storage ID";
			}
			console.log(`File has been pushed to storage. ID = ${id}`);
			FileApi.rememberFileInLocalStorage(file, id);
			return id;
		} catch (err: any) {
			EventAnalytics.sendErrorInfo("error-pushing-file", {
				message: err.message,
				...(err.response ? { statusCode: err.response.status } : {})
			});
			console.error(`Pushing file was failed: ${err}`);
			throw err;
		}
	}

	/**
	 * @param {number} id File ID
	 * @return {Promise<{isStored: bool , fileName: string}>}
	 * */
	static async isFileStoredRemotelyAsync(id: number, abortSignal?: AbortSignal) {
		console.log(`Checking if file (id = ${id}) is stored remotely...`);
		const url = FileApi.checkEndpoint;

		console.log(`GET-request url: ${url}`);
		const response = await axiosClient.axiosClient.get(url, {
			headers: network.standardHeaders(),
			params: { file_obj_id: id },
			signal: abortSignal
		});
		console.log("Response: ", response);
		if (response.status > 399) {
			throw `Storage Error ${response.status} ${response.statusText}`;
		}

		const responseJson = response.data;
		console.log("Response Json: ", responseJson);

		if (responseJson.length < 1) {
			console.log("Response is empty, so file is NOT stored.");
			return { isStored: false, fileName: "" };
		}

		const asset = responseJson[0].S3Objects;

		const resolveObj = {
			isStored: asset.status === "on_s3",
			fileName: asset.file_name
		};
		console.log(`isStored: ${resolveObj.isStored}, fileName: ${resolveObj.fileName}`);

		return resolveObj;
	}

	/**
	 * @param {File} file File to sync with storage
	 * @param options {Object} Options for file upload
	 * @param options.abortSignal {AbortSignal} abortSignal
	 * @param options.temporary {boolean} Mark file as temporary
	 * @return {Promise<number>} File storage ID
	 * */
	static async syncFileWithStorageAsync(
		file: IFileType,
		options?: { abortSignal?: AbortSignal; temporary?: boolean }
	) {
		let fileId = FileApi.getFileIdInLocalStorage(file);
		if (file.isStoredRemote && fileId) return fileId;
		if (fileId) {
			const check = await FileApi.isFileStoredRemotelyAsync(fileId, options?.abortSignal);
			if (check.isStored) {
				file.isStoredRemote = true;
				return fileId;
			}
		}

		fileId = await FileApi.pushFileAsync(file, options);
		return fileId;
	}

	/**
	 * Upload file immediately as temporary when added to node
	 * This prevents issues with moved/deleted files
	 * @param file File to upload
	 * @param onProgress Optional progress callback
	 * @return Promise with fileId and updated file object
	 */
	static async uploadFileImmediately(
		file: IFileType,
		onProgress?: (progress: number) => void
	): Promise<{ fileId: number; file: IFileType }> {
		try {
			// Check if already uploaded
			if (file.fileId && file.isStoredRemote && file.originalSrc?.startsWith("http")) {
				return { fileId: file.fileId, file };
			}

			// Upload as temporary file
			const fileId = await FileApi.syncFileWithStorageAsync(file, { temporary: true });

			// Get download link from S3
			const downloadLink = await FileApi.getLinkToAwsByFileId(fileId);

			// Mark file as stored remotely and update src to S3 link
			file.fileId = fileId;
			file.isStoredRemote = true;
			file.originalSrc = downloadLink.download_link;

			if (onProgress) {
				onProgress(100);
			}

			return { fileId, file };
		} catch (error) {
			console.error("Failed to upload file immediately:", error);
			throw error;
		}
	}

	/**
	 * @param {IFileType} file File to sync with storage
	 * @return {Promise<number>} File storage ID
	 * */
	static getDangerousLinkFromFile(file: IFileType) {
		return file.originalSrc;
	}
	/**
	 * @param {IFileType} file File to sync with storage
	 * @return {Promise<number>} File storage ID
	 * */
	static setDangerousLinkFromFile(file: IFileType, src: string) {
		file.originalSrc = src;
	}

	/**
	 * @param {File} file File to sync with storage
	 * @return {Promise<IFileType>} File
	 * */
	static async getFileFromAws(
		id: number,
		urlToAws: DownloadLinkResponse,
		abortSignal?: AbortSignal,
		isRetry = false
	): Promise<IFileType> {
		try {
			const queryParams = isRetry ? `&random=${Math.random()}` : "";
			const url =
				isRetry && config.GRAYLOG_BUILD_NAME === "prod"
					? urlToAws.download_link_proxy
					: urlToAws.download_link + queryParams;

			// Create timeout controller for 1 minute timeout
			const timeoutController = new AbortController();
			let timeoutId: NodeJS.Timeout | undefined;
			if (!isRetry && config.GRAYLOG_BUILD_NAME === "prod") {
				timeoutId = setTimeout(() => timeoutController.abort(), 60000);
			}

			// Combine original abort signal with timeout signal
			const combinedController = new AbortController();
			const combinedSignal = combinedController.signal;

			if (abortSignal) {
				abortSignal.addEventListener("abort", () => combinedController.abort());
			}
			timeoutController.signal.addEventListener("abort", () => combinedController.abort());

			const fileResponseFromAws = await fetch(url, {
				cache: isRetry ? "no-cache" : "default",
				signal: combinedSignal,
				headers: {}
			});

			// Clear timeout if request completes successfully
			clearTimeout(timeoutId);
			if (fileResponseFromAws.status > 399) {
				throw `Storage Error ${fileResponseFromAws.status} ${fileResponseFromAws.statusText}`;
			}
			const blob = await fileResponseFromAws.blob();

			const extension = urlToAws.file_name.split(".").pop();
			const mimeType = getMimeTypeByExtension(extension);
			const file = blobToFile(blob, urlToAws.file_name, mimeType);
			file.originalSrc = urlToAws.download_link;
			await addHeightWidthToFile(file);

			FileApi.rememberFileInLocalStorage(file, id);

			return file;
		} catch (e) {
			console.log(e);

			// Check if error is due to timeout or abort
			const isTimeoutError = isAbortError(e)! && !isRetry;

			EventAnalytics.sendErrorInfo("error-pulling-file-by-link", {
				message:
					typeof e === "string" ? e : e?.toString?.() || "error-pulling-file-by-link",
				fileId: id,
				url: isRetry ? urlToAws?.download_link_proxy : urlToAws?.download_link,
				fileName: urlToAws?.file_name,
				isRetry: `${isRetry}`,
				isTimeout: `${isTimeoutError}`
			});

			// Retry with proxy if not already retrying and either got timeout or other error
			if (!isRetry) {
				console.log(
					`Retrying with proxy link due to ${isTimeoutError ? "timeout" : "error"}`
				);
				return FileApi.getFileFromAws(id, urlToAws, abortSignal, true);
			}
			throw e;
		}
	}

	/**
	 * @description get file object from thirdParty url
	 * @param imageUrl
	 * @returns
	 */
	static async getFileFromImageSrc(
		imageUrl: string,
		forcedName?: string,
		signal?: AbortSignal
	): Promise<IFileType> {
		try {
			const response = await fetch(imageUrl, {
				headers: {},
				signal
			});
			const blob = await response.blob();
			const url = new URL(imageUrl);
			const fileName = url.pathname.split("/").pop() as string;
			const file = new File([blob], forcedName || fileName, {
				type: blob.type
			});
			await addHeightWidthToFile(file);
			return file;
		} catch (e) {
			console.log(e);
			EventAnalytics.sendErrorInfo("error-pulling-file-by-external-link", {
				message: getPrivateMessageFromError(e) || "error-pulling-file-by-external-link",
				url: imageUrl
			});
			throw e;
		}
	}
}
